import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Fingerprint, Mail, Upload, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";
import { toast } from "sonner";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("google"); // google | email
  const [authMode, setAuthMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const isProton = mode === "proton";
    setSubmitting(true);
    try {
      let res;
      if (authMode === "register") {
        if (!name.trim() || !email.trim() || password.length < 6) {
          toast.error("Enter your name, email and a password of at least 6 characters");
          setSubmitting(false);
          return;
        }
        if (!isProton && !idFile) {
          toast.error("Please upload your ID card or passport to register");
          setSubmitting(false);
          return;
        }
        const fd = new FormData();
        fd.append("name", name.trim());
        fd.append("email", email.trim());
        fd.append("password", password);
        fd.append("channel", isProton ? "proton" : "email");
        if (!isProton && idFile) fd.append("id_document", idFile);
        res = await api.post("/auth/register", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Account created");
      } else {
        res = await api.post("/auth/login", { email: email.trim(), password });
        toast.success("Welcome back");
      }
      setUser(res.data);
      navigate(res.data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0C] overflow-hidden grain">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#00F0FF] opacity-[0.12] blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-[#FF007F] opacity-[0.12] blur-[120px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/13800328/pexels-photo-13800328.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <header className="flex items-center justify-between py-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#00F0FF] flex items-center justify-center neon-glow-cyan">
              <Fingerprint className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl tracking-tighter">SIGNET</span>
          </div>
          <span className="font-mono-sig text-[10px] tracking-[0.3em] text-[#52525B] uppercase hidden sm:block">
            Crypto Card Network
          </span>
        </header>

        <div className="max-w-xl mx-auto pt-10 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h1 className="font-display font-black text-4xl sm:text-6xl leading-[0.95] tracking-tighter mb-8">
              Your money,
              <br />
              <span className="text-[#00F0FF]">crypto</span>
              <br />
              signed.
            </h1>

            {/* Auth channel toggle */}
            <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 mb-6 backdrop-blur-xl" data-testid="auth-channel-toggle">
              <button
                data-testid="channel-google"
                onClick={() => setMode("google")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "google" ? "bg-white text-black" : "text-[#A0A0AB] hover:text-white"}`}
              >
                Google
              </button>
              <button
                data-testid="channel-email"
                onClick={() => setMode("email")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "email" ? "bg-[#00F0FF] text-black" : "text-[#A0A0AB] hover:text-white"}`}
              >
                Email
              </button>
              <button
                data-testid="channel-proton"
                onClick={() => setMode("proton")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "proton" ? "bg-[#8A6EFF] text-black" : "text-[#A0A0AB] hover:text-white"}`}
              >
                Proton Mail
              </button>
            </div>

            {mode === "google" ? (
              <div>
                <motion.button
                  data-testid="google-login-button"
                  onClick={handleGoogleLogin}
                  whileTap={{ scale: 0.96 }}
                  whileHover={{ y: -2 }}
                  className="group inline-flex items-center gap-3 rounded-full bg-white text-black font-semibold px-7 py-4 transition-colors hover:bg-[#00F0FF]"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.22V7.04H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.19 14.97 0 12 0A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z" />
                  </svg>
                  Continue with Google
                </motion.button>
                <p className="mt-5 font-mono-sig text-[11px] text-[#52525B]">
                  First account to join becomes the network administrator.
                </p>
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl" data-testid="email-auth-form">
                <div className="flex items-center gap-2 mb-5">
                  <Mail className={`w-4 h-4 ${mode === "proton" ? "text-[#8A6EFF]" : "text-[#00F0FF]"}`} />
                  <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">
                    {mode === "proton"
                      ? (authMode === "register" ? "Proton Mail · no ID needed" : "Proton Mail login")
                      : (authMode === "register" ? "Create an account" : "Email login")}
                  </span>
                </div>

                {authMode === "register" && (
                  <div className="mb-4">
                    <label className="block text-xs text-[#A0A0AB] mb-1.5">Full name</label>
                    <Input
                      data-testid="email-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Doe"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-xs text-[#A0A0AB] mb-1.5">Email</label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs text-[#A0A0AB] mb-1.5">Password</label>
                  <Input
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                {authMode === "register" && mode !== "proton" && (
                  <div className="mb-5">
                    <label className="block text-xs text-[#A0A0AB] mb-1.5">
                      ID card or passport <span className="text-[#FF007F]">*</span>
                    </label>
                    <label
                      data-testid="id-document-label"
                      className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-white/20 bg-white/5 px-4 py-3 text-sm text-[#A0A0AB] hover:border-[#00F0FF]/50 transition-colors"
                    >
                      <Upload className="w-4 h-4 shrink-0" />
                      <span className="truncate">{idFile ? idFile.name : "Upload your ID card or passport (JPG, PNG, PDF)"}</span>
                      <input
                        data-testid="id-document-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,application/pdf"
                        onChange={(e) => setIdFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    <p className="mt-1.5 font-mono-sig text-[10px] text-[#52525B]">
                      Required once for identity verification. Only the network admin can view it.
                    </p>
                  </div>
                )}

                <Button
                  data-testid="email-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold h-11"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : authMode === "register" ? (
                    "Register & continue"
                  ) : (
                    "Log in"
                  )}
                </Button>

                <button
                  type="button"
                  data-testid="toggle-auth-mode"
                  onClick={() => setAuthMode(authMode === "register" ? "login" : "register")}
                  className="mt-4 w-full text-center text-xs text-[#A0A0AB] hover:text-[#00F0FF] transition-colors"
                >
                  {authMode === "register" ? "Already have an account? Log in" : "New here? Create an account"}
                </button>
              </form>
            )}
          </motion.div>
        </div>

        <div className="mt-10 flex justify-center">
          <button
            data-testid="verify-card-link"
            onClick={() => navigate("/verify")}
            className="group inline-flex items-center gap-2 rounded-full border border-[#00FF9D]/30 bg-[#00FF9D]/[0.06] px-5 py-2.5 text-sm text-[#00FF9D] hover:bg-[#00FF9D]/12 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            Verify a card&apos;s authenticity — no account needed
          </button>
        </div>

        <footer data-testid="made-by-credit" className="mt-10 pb-10 text-center">
          <p className="font-mono-sig text-xs tracking-[0.3em] uppercase text-[#A0A0AB]">
            Made by <span className="text-[#00F0FF]">Nothing Guilty</span>
          </p>
        </footer>
      </div>
    </div>
  );
}
